import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete,
} from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../../utils/api";
import BulkFileUpload from "../../Scope1/FugitiveEmmissions/BulkFileUpload";
import AddTables from "../../Masters/Scope1/ProductMaster/AddTables";
import { productCategoryOptions, typeofemissions } from "../../../utils/constant";
import { FUEL_TYPE_OPTIONS, FUEL_UNIT_BY_TYPE } from "../../../utils/fuelConstants";

const EXCEL_PRODUCT_ID_KEY = "Product ID / SKU";
/** Dropdown value only — separate from excel-entered product id (Natural Gas selectedEquipment pattern). */
const SELECTED_PRODUCT_KEY = "selectedProductId";
const ADD_NEW_PRODUCT_VALUE = "add_new";
/** Matches `Masters/Scope1/ProductMaster/Add.jsx` SOLD_UNIT_OPTIONS */
const SOLD_UNIT_OPTIONS = ["Pieces", "MT", "KL"];
const SAMPLE_BASIS_OPTIONS = ["Mass", "Volume", "Operational", "Bulk"];
const SAMPLE_UNITS_BY_BASIS = {
  Mass: ["Gram", "Kilogram", "Metric Tonne", "Milligram"],
  Volume: ["Milliliter", "Liter", "Cubic Meter", "Cubic Centimeter"],
  Bulk: [
    "Per Batch",
    "Per Lot",
    "Per Unit of Product",
    "Per Drum / Container",
    "Per Reactor Cycle",
    "Per Run",
  ],
  Operational: [
    "Per Machine",
    "Per Equipment",
    "Per Production Line",
    "Per Facility",
  ],
};
const PROCESS_TABLE_INPUT = [
  { title: "Process Name", filedName: "processes", placeholder: "Process Name" },
  { title: "Emission", field: "emissions", placeholder: "Emission" },
  { title: "Quantity", field: "quantity", type: "number", placeholder: "Enter Quantity" },
  { title: "Unit", field: "unit", type: "select", placeholder: "Unit" },
];
const PROCESS_EMISSION_UNITS = ["Metric Tonne", "Kilogram", "Gram", "Milligram"];

const BULK_UPLOAD_JSON_SNACK_DURATION = 4000;

const getEnteredProductId = (row) =>
  row?.productId ||
  row?.productID ||
  row?.productIdOrSku ||
  row?.["Product ID / SKU"] ||
  row?.["Product ID"] ||
  row?.["productId"] ||
  row?.["productID"] ||
  "";

const getEnteredProductName = (row) =>
  row?.productName ||
  row?.["Product Name"] ||
  row?.["productName"] ||
  row?.name ||
  "";

const getMatchedFlag = (row) => {
  if (!row) return false;
  if (typeof row.productMatched === "boolean") return row.productMatched;
  if (typeof row.productIdMatched === "boolean") return row.productIdMatched;
  if (typeof row.productIDMatched === "boolean") return row.productIDMatched;
  if (typeof row.matched === "boolean") return row.matched;
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  return matchedKey ? Boolean(row[matchedKey]) : false;
};

const setMatchedFlag = (row, next) => {
  if (!row) return row;
  if (typeof row.productMatched === "boolean")
    return { ...row, productMatched: next };
  if (typeof row.productIdMatched === "boolean")
    return { ...row, productIdMatched: next };
  if (typeof row.productIDMatched === "boolean")
    return { ...row, productIDMatched: next };
  if (typeof row.matched === "boolean") return { ...row, matched: next };
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  if (matchedKey) return { ...row, [matchedKey]: next };
  return { ...row, matched: next };
};

const BulkUploadReview = ({
  matchData = [],
  setMatchData,
  bulkUploadTable,
  onClose,
  onConfirm,
  formDataBulkUpload = [],
  setFormDataBulkUpload,
  setsnackOpen,
  bulkUploadUrl,
}) => {
  const [masterProducts, setMasterProducts] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [finalJson, setFinalJson] = useState(formDataBulkUpload);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] =
    useState(false);
  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none");
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addRowIndex, setAddRowIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [addProductFormData, setAddProductFormData] = useState({});
  const [addProductFormErrors, setAddProductFormErrors] = useState({});
  const [addProductTableData, setAddProductTableData] = useState({});

  const buildProcessTable = (processes = []) =>
    (processes || []).map((process, index) => ({
      index,
      processName: process?.processName || "",
      airEmissions: process?.airEmissions || [],
      emissions:
        Array.isArray(process?.emissions) && process.emissions.length > 0
          ? process.emissions.map((emission) => ({
            emissionsName: emission?.emissionsName || emission?.name || "",
            quantity: emission?.quantity || "",
            unit: emission?.unit || "",
          }))
          : [{ emissionsName: "", quantity: "", unit: "" }],
      colspan: process?.emissions?.length || 1,
    }));

  const getSafeProcessesForAddTable = (index) => {
    const processes = addProductFormData?.[index]?.processes;
    if (Array.isArray(processes) && processes.length > 0) return processes;
    return [
      {
        processName: "",
        airEmissions: [],
        emissions: [{ emissionsName: "", quantity: "", unit: "" }],
      },
    ];
  };

  useEffect(() => {
    if (Array.isArray(formDataBulkUpload)) setFinalJson(formDataBulkUpload);
  }, [formDataBulkUpload]);

  useEffect(() => {
    setAddProductTableData((prev) => {
      const next = { ...prev };
      Object.keys(addProductFormData || {}).forEach((key) => {
        const row = addProductFormData[key];
        if (row?.processes) {
          next[key] = buildProcessTable(row.processes);
        }
      });
      return next;
    });
  }, [addProductFormData]);

  const fetchMasters = async () => {
    setLoadingMasters(true);
    try {
      const queryParams = new URLSearchParams({
        limit: "1000",
        page: "1",
      }).toString();
      const res = await get(`/master/product/getAll?${queryParams}`);
      const products = res?.data?.data || [];
      setMasterProducts(products);
      return products;
    } catch (e) {
      setMasterProducts([]);
      return [];
    } finally {
      setLoadingMasters(false);
    }
  };

  useEffect(() => {
    if (bulkUploadTable) fetchMasters();
  }, [bulkUploadTable]);

  const productOptions = useMemo(() => {
    return (masterProducts || [])
      .filter((p) => p?._id && (p?.productId || p?.productName))
      .map((p) => ({
        id: p._id,
        label: p?.productId
          ? `${p.productId} - ${p.productName || "Unnamed"}`
          : p?.productName || "Unnamed",
      }));
  }, [masterProducts]);

  const getSelectedProductIdForIndex = (index) => {
    const row = formDataBulkUpload?.[index];
    return String(row?.[SELECTED_PRODUCT_KEY] ?? "");
  };

  const hasValidMasterSelection = (index) => {
    const selected = getSelectedProductIdForIndex(index);
    return Boolean(selected) && selected !== ADD_NEW_PRODUCT_VALUE;
  };

  const setSelectedProductForIndex = (index, nextValue) => {
    const selectedMaster = (masterProducts || []).find(
      (item) => item?._id === nextValue
    );
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = arr[index] || {};
      const next = {
        ...existing,
        [SELECTED_PRODUCT_KEY]: nextValue,
      };
      if (nextValue && nextValue !== ADD_NEW_PRODUCT_VALUE) {
        if (selectedMaster?._id) {
          next.productMasterId = selectedMaster._id;
          next[EXCEL_PRODUCT_ID_KEY] =
            selectedMaster.productId || existing?.[EXCEL_PRODUCT_ID_KEY] || "";
          next["Product Name"] =
            selectedMaster.productName || existing?.["Product Name"] || "";
        }
      } else if (nextValue === "") {
        const entered = getEnteredProductId(matchData?.[index]);
        if (entered) next[EXCEL_PRODUCT_ID_KEY] = entered;
      }
      arr[index] = next;
      return arr;
    });
  };

  const disableConfirm = useMemo(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return true;
    return matchData.some((row, idx) => {
      const matched = getMatchedFlag(row);
      if (matched) return false;
      return !hasValidMasterSelection(idx);
    });
  }, [matchData, formDataBulkUpload]);

  const openAddProductInline = (index) => {
    const enteredId = String(getEnteredProductId(matchData?.[index]) || "").trim();
    const enteredName = String(getEnteredProductName(matchData?.[index]) || "").trim();
    setAddProductFormData((prev) => ({
      ...prev,
      [index]: {
        productId: enteredId,
        productName: enteredName || enteredId,
        category: "",
        description: "",
        soldUnit: "",
        averageWeightPerUnit: "",
        averageAnnnualEnergyConsumption: "",
        frequency: "",
        lifetime: "",
        disposalMethod: "",
        sampleUsedForTesting: "",
        sampleBasis: "",
        sampleQuantity: "",
        unit: "MWh",
        usePhaseFuelEntries: [{ fuelName: "", quantity: "", unit: "" }],
        electricityUsedForMaintenance: "",
        maintenanceFuelEntries: [{ fuelName: "", quantity: "", unit: "" }],
        processes: [
          {
            processName: "",
            airEmissions: [],
            emissions: [{ emissionsName: "", quantity: "", unit: "" }],
          },
        ],
      },
    }));
    setAddProductTableData((prev) => ({
      ...prev,
      [index]: [
        {
          index: 0,
          processName: "",
          airEmissions: [],
          emissions: [{ emissionsName: "", quantity: "", unit: "" }],
          colspan: 1,
        },
      ],
    }));
    setAddRowIndex(index);
    setExpandedRows((prev) => ({ ...prev, [index]: true }));
  };

  const handleAddProductFieldChange = (index, field, value) => {
    const decimalFields = new Set([
      "averageWeightPerUnit",
      "averageAnnnualEnergyConsumption",
      "lifetime",
      "sampleQuantity",
      "electricityUsedForMaintenance",
    ]);
    if (decimalFields.has(field) && value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    if (field === "frequency" && value !== "" && !/^\d*$/.test(value)) return;

    setAddProductFormData((prev) => {
      const currentRow = prev[index] || {};
      const updates = { [field]: value };
      if (field === "productId") updates.productName = value;
      if (field === "category") {
        updates.disposalMethod = "";
      }
      if (field === "sampleBasis") {
        updates.unit = "";
      }
      return {
        ...prev,
        [index]: { ...currentRow, ...updates },
      };
    });
    setAddProductFormErrors((prev) => {
      const clearedKeys = { [field]: "" };
      if (field === "category") clearedKeys.disposalMethod = "";
      if (field === "sampleBasis") clearedKeys.unit = "";
      return {
        ...prev,
        [index]: { ...(prev[index] || {}), ...clearedKeys },
      };
    });
  };

  const handleProcessFieldChange = (index, processIndex, field, value) => {
    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const processes = Array.isArray(row.processes)
        ? [...row.processes]
        : [{ processName: "", airEmissions: [], emissions: [{ emissionsName: "", quantity: "", unit: "" }] }];
      const currentProcess = processes[processIndex] || {
        processName: "",
        airEmissions: [],
        emissions: [{ emissionsName: "", quantity: "", unit: "" }],
      };
      const nextProcess = { ...currentProcess, [field]: value };
      if (field === "airEmissions") {
        nextProcess.emissions = (value || []).map((name) => ({
          emissionsName: name,
          quantity: "",
          unit: "",
        }));
      }
      processes[processIndex] = nextProcess;
      return {
        ...prev,
        [index]: {
          ...row,
          processes,
        },
      };
    });
    setAddProductTableData((prev) => {
      const next = { ...prev };
      const latestProcesses = (addProductFormData[index]?.processes || []).map((p, pIdx) =>
        pIdx === processIndex
          ? {
            ...p,
            [field]: value,
            ...(field === "airEmissions"
              ? {
                emissions: (value || []).map((name) => ({
                  emissionsName: name,
                  quantity: "",
                  unit: "",
                })),
              }
              : {}),
          }
          : p
      );
      next[index] = buildProcessTable(latestProcesses);
      return next;
    });
    setAddProductFormErrors((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        [`processes.processName.${processIndex}`]: "",
        [`airEmissions.${processIndex}`]: "",
      },
    }));
  };

  const handleAddProcess = (index) => {
    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const processes = Array.isArray(row.processes) ? [...row.processes] : [];
      processes.push({
        processName: "",
        airEmissions: [],
        emissions: [{ emissionsName: "", quantity: "", unit: "" }],
      });
      return { ...prev, [index]: { ...row, processes } };
    });
    setAddProductTableData((prev) => {
      const existing = Array.isArray(prev[index]) ? [...prev[index]] : [];
      existing.push({
        index: existing.length,
        processName: "",
        airEmissions: [],
        emissions: [{ emissionsName: "", quantity: "", unit: "" }],
        colspan: 1,
      });
      return { ...prev, [index]: existing };
    });
  };

  const handleRemoveProcess = (index, processIndex) => {
    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const processes = (row.processes || []).filter((_, i) => i !== processIndex);
      const safeProcesses =
        processes.length > 0
          ? processes
          : [{ processName: "", airEmissions: [], emissions: [{ emissionsName: "", quantity: "", unit: "" }] }];
      return { ...prev, [index]: { ...row, processes: safeProcesses } };
    });
    setAddProductTableData((prev) => {
      const remaining = (prev[index] || []).filter((_, i) => i !== processIndex);
      const safeTable =
        remaining.length > 0
          ? remaining.map((item, i) => ({ ...item, index: i }))
          : [
            {
              index: 0,
              processName: "",
              airEmissions: [],
              emissions: [{ emissionsName: "", quantity: "", unit: "" }],
              colspan: 1,
            },
          ];
      return { ...prev, [index]: safeTable };
    });
  };

  const handleUsePhaseFuelEntryChange = (index, fuelIndex, field, value) => {
    if (field === "quantity" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const entries = Array.isArray(row.usePhaseFuelEntries)
        ? [...row.usePhaseFuelEntries]
        : [{ fuelName: "", quantity: "", unit: "" }];
      const current = entries[fuelIndex] || { fuelName: "", quantity: "", unit: "" };
      const nextEntry = { ...current, [field]: value };
      if (field === "fuelName") nextEntry.unit = "";
      entries[fuelIndex] = nextEntry;
      return {
        ...prev,
        [index]: {
          ...row,
          usePhaseFuelEntries: entries,
        },
      };
    });
    setAddProductFormErrors((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        [`usePhaseFuelEntries.${field}.${fuelIndex}`]: "",
      },
    }));
  };

  const handleAddUsePhaseFuelEntry = (index) => {
    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const entries = Array.isArray(row.usePhaseFuelEntries)
        ? [...row.usePhaseFuelEntries]
        : [{ fuelName: "", quantity: "", unit: "" }];
      const isFuelEntryComplete = (entry) =>
        entry?.fuelName?.toString().trim() !== "" &&
        entry?.quantity?.toString().trim() !== "" &&
        entry?.unit?.toString().trim() !== "";
      if (!entries.every((entry) => isFuelEntryComplete(entry))) return prev;
      if (entries.length >= 2) return prev;
      return {
        ...prev,
        [index]: {
          ...row,
          usePhaseFuelEntries: [...entries, { fuelName: "", quantity: "", unit: "" }],
        },
      };
    });
  };

  const handleRemoveUsePhaseFuelEntry = (index, fuelIndex) => {
    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const entries = Array.isArray(row.usePhaseFuelEntries)
        ? row.usePhaseFuelEntries.filter((_, idx) => idx !== fuelIndex)
        : [];
      return {
        ...prev,
        [index]: {
          ...row,
          usePhaseFuelEntries:
            entries.length > 0 ? entries : [{ fuelName: "", quantity: "", unit: "" }],
        },
      };
    });
  };

  const handleMaintenanceFuelEntryChange = (index, fuelIndex, field, value) => {
    if (field === "quantity" && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const entries = Array.isArray(row.maintenanceFuelEntries)
        ? [...row.maintenanceFuelEntries]
        : [{ fuelName: "", quantity: "", unit: "" }];
      const current = entries[fuelIndex] || { fuelName: "", quantity: "", unit: "" };
      const nextEntry = { ...current, [field]: value };
      if (field === "fuelName") nextEntry.unit = "";
      entries[fuelIndex] = nextEntry;
      return {
        ...prev,
        [index]: {
          ...row,
          maintenanceFuelEntries: entries,
        },
      };
    });
    setAddProductFormErrors((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        [`maintenanceFuelEntries.${field}.${fuelIndex}`]: "",
      },
    }));
  };

  const handleAddMaintenanceFuelEntry = (index) => {
    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const entries = Array.isArray(row.maintenanceFuelEntries)
        ? [...row.maintenanceFuelEntries]
        : [{ fuelName: "", quantity: "", unit: "" }];
      const isFuelEntryComplete = (entry) =>
        entry?.fuelName?.toString().trim() !== "" &&
        entry?.quantity?.toString().trim() !== "" &&
        entry?.unit?.toString().trim() !== "";
      if (!entries.every((entry) => isFuelEntryComplete(entry))) return prev;
      if (entries.length >= 2) return prev;
      return {
        ...prev,
        [index]: {
          ...row,
          maintenanceFuelEntries: [...entries, { fuelName: "", quantity: "", unit: "" }],
        },
      };
    });
  };

  const handleRemoveMaintenanceFuelEntry = (index, fuelIndex) => {
    setAddProductFormData((prev) => {
      const row = prev[index] || {};
      const entries = Array.isArray(row.maintenanceFuelEntries)
        ? row.maintenanceFuelEntries.filter((_, idx) => idx !== fuelIndex)
        : [];
      return {
        ...prev,
        [index]: {
          ...row,
          maintenanceFuelEntries:
            entries.length > 0 ? entries : [{ fuelName: "", quantity: "", unit: "" }],
        },
      };
    });
  };

  const getSelectedFuelNames = (entries = [], currentFuelIndex = -1) =>
    new Set(
      (Array.isArray(entries) ? entries : [])
        .filter((_, idx) => idx !== currentFuelIndex)
        .map((entry) => entry?.fuelName?.toString().trim())
        .filter(Boolean)
    );

  const isProcessTableErrorKey = (key = "") =>
    key.startsWith("processes.") ||
    key.startsWith("airEmissions.") ||
    key.startsWith("emissionsName_") ||
    key.startsWith("quantity_") ||
    key.startsWith("unit_");

  const getProcessTableErrors = (index) => {
    const rowErrors = addProductFormErrors[index] || {};
    return Object.fromEntries(
      Object.entries(rowErrors).filter(([key]) => isProcessTableErrorKey(key))
    );
  };

  const validateAddProductForm = (index) => {
    const row = addProductFormData[index] || {};
    const nextErrors = {};
    const isFuelEntryComplete = (entry) =>
      entry?.fuelName?.toString().trim() !== "" &&
      entry?.quantity?.toString().trim() !== "" &&
      entry?.unit?.toString().trim() !== "";
    if (!row.productId?.toString().trim()) nextErrors.productId = "This field is required";
    if (!row.productName?.toString().trim()) nextErrors.productName = "This field is required";
    if (!row.category?.toString().trim()) nextErrors.category = "This field is required";
    if (!row.description?.toString().trim()) nextErrors.description = "This field is required";
    if (!row.soldUnit?.toString().trim()) nextErrors.soldUnit = "Sold unit is required.";
    if (!row.averageWeightPerUnit?.toString().trim())
      nextErrors.averageWeightPerUnit = "This field is required";
    if (!row.averageAnnnualEnergyConsumption?.toString().trim())
      nextErrors.averageAnnnualEnergyConsumption = "This field is required";
    if (!row.electricityUsedForMaintenance?.toString().trim())
      nextErrors.electricityUsedForMaintenance = "This field is required";
    if (!row.frequency?.toString().trim()) {
      nextErrors.frequency = "This field is required";
    } else if (!/^\d+$/.test(String(row.frequency))) {
      nextErrors.frequency = "Only whole numbers are allowed.";
    }
    if (!row.lifetime?.toString().trim()) nextErrors.lifetime = "This field is required";
    if (!row.disposalMethod?.toString().trim()) nextErrors.disposalMethod = "This field is required";
    if (!row.sampleBasis?.toString().trim()) nextErrors.sampleBasis = "This field is required";
    if (!row.sampleQuantity?.toString().trim()) nextErrors.sampleQuantity = "This field is required";
    if (!row.unit?.toString().trim()) nextErrors.unit = "This field is required";
    const usePhaseFuelEntries = Array.isArray(row.usePhaseFuelEntries)
      ? row.usePhaseFuelEntries
      : [];
    if (!Array.isArray(row.usePhaseFuelEntries) || row.usePhaseFuelEntries.length === 0) {
      nextErrors["usePhaseFuelEntries"] = "At least one fuel entry is required.";
    } else {
      usePhaseFuelEntries.forEach((entry, fuelIndex) => {
        const hasAnyValue =
          entry?.fuelName?.toString().trim() ||
          entry?.quantity?.toString().trim() ||
          entry?.unit?.toString().trim();
        if (!hasAnyValue && fuelIndex > 0) return;
        if (!entry?.fuelName?.toString().trim()) {
          nextErrors[`usePhaseFuelEntries.fuelName.${fuelIndex}`] = "Fuel name is required.";
        }
        if (!entry?.quantity?.toString().trim()) {
          nextErrors[`usePhaseFuelEntries.quantity.${fuelIndex}`] = "Quantity is required.";
        }
        if (!entry?.unit?.toString().trim()) {
          nextErrors[`usePhaseFuelEntries.unit.${fuelIndex}`] = "Unit is required.";
        }
      });
      const selectedUsePhaseFuelNames = usePhaseFuelEntries
        .map((entry) => entry?.fuelName?.toString().trim())
        .filter(Boolean);
      if (new Set(selectedUsePhaseFuelNames).size !== selectedUsePhaseFuelNames.length) {
        nextErrors["usePhaseFuelEntries"] =
          "Fuel names in use phase must be different.";
      }
    }
    const maintenanceFuelEntries = Array.isArray(row.maintenanceFuelEntries)
      ? row.maintenanceFuelEntries
      : [];
    if (!Array.isArray(row.maintenanceFuelEntries) || row.maintenanceFuelEntries.length === 0) {
      nextErrors["maintenanceFuelEntries"] = "At least one fuel entry is required.";
    } else {
      maintenanceFuelEntries.forEach((entry, fuelIndex) => {
        const hasAnyValue =
          entry?.fuelName?.toString().trim() ||
          entry?.quantity?.toString().trim() ||
          entry?.unit?.toString().trim();
        if (!hasAnyValue && fuelIndex > 0) return;
        if (!entry?.fuelName?.toString().trim()) {
          nextErrors[`maintenanceFuelEntries.fuelName.${fuelIndex}`] = "Fuel name is required.";
        }
        if (!entry?.quantity?.toString().trim()) {
          nextErrors[`maintenanceFuelEntries.quantity.${fuelIndex}`] = "Quantity is required.";
        }
        if (!entry?.unit?.toString().trim()) {
          nextErrors[`maintenanceFuelEntries.unit.${fuelIndex}`] = "Unit is required.";
        }
      });
      const selectedMaintenanceFuelNames = maintenanceFuelEntries
        .map((entry) => entry?.fuelName?.toString().trim())
        .filter(Boolean);
      if (
        new Set(selectedMaintenanceFuelNames).size !==
        selectedMaintenanceFuelNames.length
      ) {
        nextErrors["maintenanceFuelEntries"] =
          "Fuel names in maintenance must be different.";
      }
    }
    const processes = Array.isArray(row.processes) ? row.processes : [];
    processes.forEach((process, processIndex) => {
      if (!process?.processName?.toString().trim()) {
        nextErrors[`processes.processName.${processIndex}`] = "Process name is required.";
      }
      if (!Array.isArray(process?.airEmissions) || process.airEmissions.length === 0) {
        nextErrors[`airEmissions.${processIndex}`] = "At least one emission is required.";
      }
      const emissions = Array.isArray(process?.emissions) ? process.emissions : [];
      emissions.forEach((emission, emissionIndex) => {
        if (!emission?.emissionsName?.toString().trim()) {
          nextErrors[`emissionsName_${processIndex}_${emissionIndex}`] =
            "Emission name is required.";
        }
        if (!emission?.quantity?.toString().trim()) {
          nextErrors[`quantity_${processIndex}_${emissionIndex}`] = "Quantity is required.";
        }
        if (!emission?.unit?.toString().trim()) {
          nextErrors[`unit_${processIndex}_${emissionIndex}`] = "Unit is required.";
        }
      });
    });
    const enteredProductId = row.productId?.toString().trim().toLowerCase();
    if (
      enteredProductId &&
      (masterProducts || []).some(
        (product) =>
          product?.productId?.toString().trim().toLowerCase() === enteredProductId
      )
    ) {
      nextErrors.productId = "This ID already exists.";
    }
    setAddProductFormErrors((prev) => ({ ...prev, [index]: nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const handleAddProductItem = async (index) => {
    if (!validateAddProductForm(index)) return;
    const row = addProductFormData[index];
    const toNumberOrDefault = (value, fallback = 1) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    };
    const usePhaseFuelEntriesFromForm = (row.usePhaseFuelEntries || [])
      .filter(
        (entry) =>
          entry?.fuelName?.toString().trim() &&
          entry?.quantity?.toString().trim() &&
          entry?.unit?.toString().trim()
      )
      .slice(0, 2)
      .map((entry) => ({
        fuelName: entry.fuelName,
        quantity: toNumberOrDefault(entry.quantity, 1),
        unit: entry.unit,
      }));
    const maintenanceFuelEntriesFromForm = (row.maintenanceFuelEntries || [])
      .filter(
        (entry) =>
          entry?.fuelName?.toString().trim() &&
          entry?.quantity?.toString().trim() &&
          entry?.unit?.toString().trim()
      )
      .slice(0, 2)
      .map((entry) => ({
        fuelName: entry.fuelName,
        quantity: toNumberOrDefault(entry.quantity, 1),
        unit: entry.unit,
      }));
    const selectedCategory = productCategoryOptions.find(
      (item) => item?.category === row.category
    );
    const selectedMethod = (selectedCategory?.methods || []).find(
      (method) => method?.type === row.disposalMethod
    );
    const processesFromForm = (row.processes || []).map((process) => ({
      processName: process?.processName || "",
      airEmissions: process?.airEmissions || [],
      emissions:
        Array.isArray(process?.emissions) && process.emissions.length > 0
          ? process.emissions.map((emission) => ({
            emissionsName: emission?.emissionsName || "",
            quantity: toNumberOrDefault(emission?.quantity, 1),
            unit: emission?.unit || "",
          }))
          : [],
    }));

    const payload = {
      productName: row.productName.trim(),
      productId: row.productId.trim(),
      category: row.category.trim(),
      description: row.description.trim(),
      soldUnit: String(row.soldUnit || "").trim(),
      soldUnitQuantity: "",
      weight: toNumberOrDefault(row.averageWeightPerUnit, 1),
      averageWeightPerUnit: toNumberOrDefault(row.averageWeightPerUnit, 1),
      averageAnnnualEnergyConsumption: toNumberOrDefault(
        row.averageAnnnualEnergyConsumption,
        1
      ),
      usePhaseEnergyConsumption: toNumberOrDefault(
        row.averageAnnnualEnergyConsumption,
        1
      ),
      usePhaseEnergyConsumptionUnit: "Mwh",
      energyUsedForMaintenance: toNumberOrDefault(
        row.electricityUsedForMaintenance,
        1
      ),
      energyUsedForMaintenanceUnit: "Mwh",
      energyOrFuelUsedForMaintenance: toNumberOrDefault(
        row.electricityUsedForMaintenance,
        1
      ),
      sampleBasis: row.sampleBasis || "Mass",
      sampleQuantity: toNumberOrDefault(row.sampleQuantity, 1),
      unit: row.unit || "MWh",
      frequency: toNumberOrDefault(row.frequency, 1),
      frequencyOfMaintenancePerYear: toNumberOrDefault(row.frequency, 1),
      disposalMethod: row.disposalMethod || "Landfilling",
      lifetime: toNumberOrDefault(row.lifetime, 1),
      expectedLifetimeOfTheProductYears: toNumberOrDefault(row.lifetime, 1),
      date: row.sampleUsedForTesting || new Date().toISOString().split("T")[0],
      fuelName:
        maintenanceFuelEntriesFromForm[0]?.fuelName || usePhaseFuelEntriesFromForm[0]?.fuelName || "",
      quantity:
        maintenanceFuelEntriesFromForm[0]?.quantity || usePhaseFuelEntriesFromForm[0]?.quantity || "",
      values: selectedMethod?.values || {},
      fuelEntries:
        maintenanceFuelEntriesFromForm.length > 0
          ? maintenanceFuelEntriesFromForm
          : [{ fuelName: "Natural gas", quantity: 1, unit: "M3" }],
      usePhaseFuelEntries:
        usePhaseFuelEntriesFromForm.length > 0
          ? usePhaseFuelEntriesFromForm
          : [{ fuelName: "Natural gas", quantity: 1, unit: "M3" }],
      processes:
        processesFromForm.length > 0
          ? processesFromForm
          : [
            {
              processName: "Default process",
              airEmissions: ["Carbon dioxide ; CO2"],
              emissions: [
                { emissionsName: "Carbon dioxide ; CO2", quantity: 1, unit: "Kilogram" },
              ],
            },
          ],
    };
    try {
      const response = await post("/master/product/create", payload);
      setsnackOpen?.({
        open: true,
        message: response?.data?.message || "Product added successfully",
        severity: "success",
      });
      const latestProducts = await fetchMasters();
      const created = (latestProducts || []).find(
        (p) => p?.productId?.toString().trim().toLowerCase() === row.productId.trim().toLowerCase()
      );
      if (created?._id) {
        setSelectedProductForIndex(index, created._id);
        setMatchData((prev) =>
          (prev || []).map((r, i) => (i === index ? setMatchedFlag(r, true) : r))
        );
      }
      setExpandedRows((prev) => ({ ...prev, [index]: false }));
      setAddProductFormData((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setAddProductFormErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setAddProductTableData((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    } catch (error) {
      setsnackOpen?.({
        open: true,
        message: error?.response?.data?.message || "Unable to add product",
        severity: "error",
      });
    }
  };

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
      setFinalJson((prev) =>
        Array.isArray(prev)
          ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
          : prev
      );
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    setCommonDocument(files);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({
          ...entry,
          "Document (optional)": files,
        }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry) => ({
        ...entry,
        "Document (optional)": files,
      }));
      setFinalJson(updated);
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleCommonDocumentClear = () => {
    setCommonDocument(null);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({
          ...entry,
          "Document (optional)": null,
        }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry) => ({
        ...entry,
        "Document (optional)": null,
      }));
      setFinalJson(updated);
    }
  };

  const handleIndividualDocumentChange = (index, files) => {
    setIndividualDocuments((prev) => ({ ...prev, [index]: files }));
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": files } : entry
        )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": files } : entry
      );
      setFinalJson(updated);
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleIndividualDocumentClear = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": null } : entry
        )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": null } : entry
      );
      setFinalJson(updated);
    }
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push(
        "Please select a document upload type when document upload is confirmed."
      );
    }
    if (documentUploadConfirmed && documentUploadType !== "none") {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
      }
    }
    return validationErrors;
  };

  const performUpload = async () => {
    try {
      setUploading(true);
      const dataToUse =
        Array.isArray(finalJson) && finalJson.length > 0
          ? finalJson
          : formDataBulkUpload;
      const uploadData = {
        excelData: dataToUse,
        documentUploadType,
        commonDocument,
        individualDocuments,
      };
      const res = await post(bulkUploadUrl, uploadData);

      const inserted = Number(res?.data?.inserted ?? 0);
      const skipped = Number(res?.data?.skipped ?? 0);

      if (inserted === 0) {
        setsnackOpen?.({
          open: true,
          message:
            skipped > 0
              ? `No data inserted. ${skipped} row${skipped > 1 ? "s were" : " was"} skipped. Please review and try again.`
              : res?.data?.message ||
                "No data inserted. Please review and try again.",
          severity: "error",
          autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
        });
        return;
      }

      const responseData = res?.data ?? {};
      const skippedCount = Number(responseData?.skipped ?? skipped ?? 0);
      const apiMessage = responseData?.message;
      const successMessage = "Data uploaded successfully";
      setsnackOpen?.({
        open: true,
        message:
          skippedCount > 0
            ? apiMessage ||
              `${inserted} entr${inserted === 1 ? "y" : "ies"} inserted. ${skippedCount} row${skippedCount === 1 ? "" : "s"} skipped.`
            : successMessage,
        severity: skippedCount > 0 ? "warning" : "success",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });

      onConfirm?.(responseData);
      onClose?.();
    } catch (e) {
      const errMessage = e?.response?.data?.details
        ?.map((el) => el?.error || el?.reason || el?.msg)
        ?.filter(Boolean)
        ?.join(", ");
      setsnackOpen?.({
        open: true,
        message: errMessage || e?.response?.data?.message || "Upload failed",
        severity: "error",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }
    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }
    await performUpload();
  };

  if (!bulkUploadTable) return null;

  return (
    <>
      <Dialog
        open={bulkUploadTable}
        onClose={onClose}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            margin: "16px",
            maxHeight: "calc(100% - 32px)",
            zIndex: 1000,
          },
        }}
      >
        <DialogTitle>Review Bulk Upload Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review matched and unmatched product rows. Select a valid product for
            unmatched records before confirming upload.
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Data Entered
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Master Data
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                    Action
                  </TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                      Document
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Product ID / SKU</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(matchData || []).map((row, index) => {
                  const entered = getEnteredProductId(row);
                  const matched = getMatchedFlag(row);
                  const selectedProductId = getSelectedProductIdForIndex(index);
                  const selectedIsAddNew =
                    selectedProductId === ADD_NEW_PRODUCT_VALUE;
                  return (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>{entered || "-"}</TableCell>
                        <TableCell>
                          {matched ? (
                            <Chip label="Matched" color="success" size="small" />
                          ) : (
                            <FormControl
                              size="small"
                              sx={{ minWidth: "10.5rem", maxWidth: "10.5rem" }}
                            >
                              <Select
                                value={selectedProductId || ""}
                                onChange={(e) => {
                                  const nextValue = e.target.value;
                                  if (nextValue === ADD_NEW_PRODUCT_VALUE) {
                                    setSelectedProductForIndex(
                                      index,
                                      ADD_NEW_PRODUCT_VALUE
                                    );
                                    openAddProductInline(index);
                                    setExpandedRows((prev) => ({
                                      ...prev,
                                      [index]: true,
                                    }));
                                    setMatchData((prev) =>
                                      (prev || []).map((r, i) =>
                                        i === index ? setMatchedFlag(r, false) : r
                                      )
                                    );
                                    return;
                                  }
                                  setSelectedProductForIndex(index, nextValue);
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [index]: false,
                                  }));
                                  setMatchData((prev) =>
                                    (prev || []).map((r, i) =>
                                      i === index
                                        ? setMatchedFlag(r, Boolean(nextValue))
                                        : r
                                    )
                                  );
                                }}
                                fullWidth
                                displayEmpty
                                disabled={loadingMasters}
                              >
                                <MenuItem value="" disabled>
                                  Tap to Select
                                </MenuItem>
                                <MenuItem value={ADD_NEW_PRODUCT_VALUE}>
                                  Add New Product +
                                </MenuItem>
                                {productOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.id}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell>
                          {!matched &&
                            (selectedIsAddNew || expandedRows[index] ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  const willClose = expandedRows[index];
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [index]: !prev[index],
                                  }));
                                  if (!willClose) {
                                    openAddProductInline(index);
                                  }
                                }}
                                sx={{ minWidth: "80px" }}
                              >
                                {expandedRows[index] ? "Close" : "Open"}
                              </Button>
                            ) : (
                              <Chip
                                label={
                                  hasValidMasterSelection(index)
                                    ? "Selected"
                                    : "Unmatched"
                                }
                                color={
                                  hasValidMasterSelection(index)
                                    ? "info"
                                    : "warning"
                                }
                                size="small"
                              />
                            ))}
                        </TableCell>
                        {documentUploadConfirmed &&
                          documentUploadType === "individual" && (
                            <TableCell>
                              <BulkFileUpload
                                handleChange={handleIndividualDocumentChange}
                                handleClear={handleIndividualDocumentClear}
                                filesList={
                                  individualDocuments[index] ||
                                  formDataBulkUpload?.[index]?.["Document (optional)"] ||
                                  finalJson?.[index]?.["Document (optional)"] ||
                                  []
                                }
                                index={index}
                              />
                            </TableCell>
                          )}
                      </TableRow>
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={
                            documentUploadConfirmed && documentUploadType === "individual"
                              ? 4
                              : 3
                          }
                        >
                          <Collapse
                            in={Boolean(expandedRows[index])}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                                Add New Product
                              </Typography>
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr",
                                  gap: 1.5,
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "10px",
                                  p: 1.5,
                                  backgroundColor: "#fff",
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    marginRight: 0,
                                    marginTop: "4px",
                                    fontSize: "14px",
                                    lineHeight: 1.35,
                                    textAlign: "left",
                                  },
                                }}
                              >
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Product ID / SKU
                                  </Typography>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Enter product ID"
                                    value={addProductFormData[index]?.productId || ""}
                                    InputProps={{ readOnly: true }}
                                    error={Boolean(addProductFormErrors[index]?.productId)}
                                    helperText={addProductFormErrors[index]?.productId || ""}
                                  />
                                </Box>
                                <Box>
                                  
                                  
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Product Name
                                  </Typography>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Enter product name"
                                    value={addProductFormData[index]?.productName || ""}
                                    InputProps={{ readOnly: true }}
                                    error={Boolean(addProductFormErrors[index]?.productName)}
                                    helperText={addProductFormErrors[index]?.productName || ""}
                                  />
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1", mt: 0.5 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 700,
                                      borderTop: "1px solid #e5e7eb",
                                      pt: 1.2,
                                    }}
                                  >
                                    Product Details
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Product Category
                                  </Typography>
                                  <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    SelectProps={{ displayEmpty: true }}
                                    value={addProductFormData[index]?.category || ""}
                                    onChange={(e) =>
                                      handleAddProductFieldChange(index, "category", e.target.value)
                                    }
                                    error={Boolean(addProductFormErrors[index]?.category)}
                                    helperText={addProductFormErrors[index]?.category || ""}
                                  >
                                    <MenuItem value="" disabled>
                                      Tap to Select
                                    </MenuItem>
                                    {productCategoryOptions.map((item, idx) => (
                                      <MenuItem key={idx} value={item.category}>
                                        {item.category}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Product Description
                                  </Typography>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Enter description"
                                    value={addProductFormData[index]?.description || ""}
                                    onChange={(e) =>
                                      handleAddProductFieldChange(index, "description", e.target.value)
                                    }
                                    error={Boolean(addProductFormErrors[index]?.description)}
                                    helperText={addProductFormErrors[index]?.description || ""}
                                  />
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                  Sold unit
                                  </Typography>
                                  <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    SelectProps={{ displayEmpty: true }}
                                    value={addProductFormData[index]?.soldUnit || ""}
                                    onChange={(e) =>
                                      handleAddProductFieldChange(index, "soldUnit", e.target.value)
                                    }
                                    error={Boolean(addProductFormErrors[index]?.soldUnit)}
                                    helperText={addProductFormErrors[index]?.soldUnit || ""}
                                  >
                                    <MenuItem value="" disabled>
                                      Tap to Select
                                    </MenuItem>
                                    {SOLD_UNIT_OPTIONS.map((u) => (
                                      <MenuItem key={u} value={u}>
                                        {u}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Average weight per unit of product
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder="Enter average weight"
                                      value={addProductFormData[index]?.averageWeightPerUnit || ""}
                                      onChange={(e) =>
                                        handleAddProductFieldChange(
                                          index,
                                          "averageWeightPerUnit",
                                          e.target.value
                                        )
                                      }
                                      inputProps={{ inputMode: "decimal" }}
                                      error={Boolean(addProductFormErrors[index]?.averageWeightPerUnit)}
                                      helperText={addProductFormErrors[index]?.averageWeightPerUnit || ""}
                                    />
                                    <TextField size="small" value="MT" disabled sx={{ width: "7rem" }} />
                                  </Box>
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Average annual electricity consumption in use phase of product
                                  </Typography>

                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder="Enter annual energy consumption"
                                      value={
                                        addProductFormData[index]
                                          ?.averageAnnnualEnergyConsumption || ""
                                      }
                                      onChange={(e) =>
                                        handleAddProductFieldChange(
                                          index,
                                          "averageAnnnualEnergyConsumption",
                                          e.target.value
                                        )
                                      }
                                      inputProps={{ inputMode: "decimal" }}
                                      error={Boolean(addProductFormErrors[index]?.averageAnnnualEnergyConsumption)}
                                      helperText={addProductFormErrors[index]?.averageAnnnualEnergyConsumption || ""}
                                    />
                                    <TextField size="small" value="Mwh" disabled sx={{ width: "7rem" }} />
                                  </Box>
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Fuel required for use (You can select upto two fuel type)
                                  </Typography>
                                  <Box display="flex" gap={1}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: "bold", flex: 1.3, minWidth: 180, mb: 0.1 }}
                                    >
                                      Fuel Name
                                    </Typography>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: "bold", flex: 1, minWidth: 150, mb: 0.1 }}
                                    >
                                      Quantity
                                    </Typography>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: "bold", flex: 0.8, minWidth: 130, mb: 0.1 }}
                                    >
                                      Unit
                                    </Typography>
                                  </Box>
                                  {(addProductFormData[index]?.usePhaseFuelEntries || [
                                    { fuelName: "", quantity: "", unit: "" },
                                  ]).map((fuel, fuelIndex) => (
                                    <Box
                                      display="flex"
                                      gap={1}
                                      key={`bulk-use-phase-fuel-${fuelIndex}`}
                                      sx={{ mb: 0.8 }}
                                    >
                                      <TextField
                                        select
                                        size="small"
                                        placeholder="Fuel Name"
                                        value={fuel?.fuelName || ""}
                                        onChange={(e) =>
                                          handleUsePhaseFuelEntryChange(
                                            index,
                                            fuelIndex,
                                            "fuelName",
                                            e.target.value
                                          )
                                        }
                                        sx={{ flex: 1.3, minWidth: 180 }}
                                        error={Boolean(
                                          addProductFormErrors[index]?.[
                                          `usePhaseFuelEntries.fuelName.${fuelIndex}`
                                          ]
                                        )}
                                        helperText={
                                          addProductFormErrors[index]?.[
                                          `usePhaseFuelEntries.fuelName.${fuelIndex}`
                                          ] || ""
                                        }
                                        SelectProps={{
                                          displayEmpty: true,
                                          renderValue: (selected) =>
                                            selected && selected !== "" ? selected : "Tap to select",
                                        }}
                                      >
                                        <MenuItem value="">Select fuel type</MenuItem>
                                        {FUEL_TYPE_OPTIONS.map((fuelType) => (
                                          <MenuItem
                                            key={fuelType}
                                            value={fuelType}
                                            disabled={getSelectedFuelNames(
                                              addProductFormData[index]?.usePhaseFuelEntries || [],
                                              fuelIndex
                                            ).has(fuelType)}
                                          >
                                            {fuelType}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                      <TextField
                                        size="small"
                                        placeholder="Quantity"
                                        value={fuel?.quantity || ""}
                                        onChange={(e) =>
                                          handleUsePhaseFuelEntryChange(
                                            index,
                                            fuelIndex,
                                            "quantity",
                                            e.target.value
                                          )
                                        }
                                        inputProps={{ inputMode: "decimal" }}
                                        sx={{ flex: 1, minWidth: 150 }}
                                        error={Boolean(
                                          addProductFormErrors[index]?.[
                                          `usePhaseFuelEntries.quantity.${fuelIndex}`
                                          ]
                                        )}
                                        helperText={
                                          addProductFormErrors[index]?.[
                                          `usePhaseFuelEntries.quantity.${fuelIndex}`
                                          ] || ""
                                        }
                                      />
                                      <TextField
                                        select
                                        size="small"
                                        placeholder="Unit"
                                        value={fuel?.unit || ""}
                                        onChange={(e) =>
                                          handleUsePhaseFuelEntryChange(
                                            index,
                                            fuelIndex,
                                            "unit",
                                            e.target.value
                                          )
                                        }
                                        sx={{ flex: 0.8, minWidth: 130 }}
                                        error={Boolean(
                                          addProductFormErrors[index]?.[
                                          `usePhaseFuelEntries.unit.${fuelIndex}`
                                          ]
                                        )}
                                        helperText={
                                          addProductFormErrors[index]?.[
                                          `usePhaseFuelEntries.unit.${fuelIndex}`
                                          ] || ""
                                        }
                                        SelectProps={{
                                          displayEmpty: true,
                                          renderValue: (selected) =>
                                            selected && selected !== "" ? selected : "Tap to select",
                                        }}
                                      >
                                        <MenuItem value="">Select unit</MenuItem>
                                        {(FUEL_UNIT_BY_TYPE[fuel?.fuelName] || []).map((u) => (
                                          <MenuItem key={u} value={u}>
                                            {u}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                      {(addProductFormData[index]?.usePhaseFuelEntries || []).length > 1 && (
                                        <IconButton
                                          color="error"
                                          onClick={() => handleRemoveUsePhaseFuelEntry(index, fuelIndex)}
                                          sx={{ alignSelf: "center" }}
                                        >
                                          <Delete />
                                        </IconButton>
                                      )}
                                    </Box>
                                  ))}
                                  {addProductFormErrors[index]?.["usePhaseFuelEntries"] && (
                                    <Typography variant="caption" color="error">
                                      {addProductFormErrors[index]?.["usePhaseFuelEntries"]}
                                    </Typography>
                                  )}
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      variant="outlined"
                                      onClick={() => handleAddUsePhaseFuelEntry(index)}
                                      disabled={
                                        !(
                                          (addProductFormData[index]?.usePhaseFuelEntries || []).every(
                                            (entry) =>
                                              entry?.fuelName?.toString().trim() !== "" &&
                                              entry?.quantity?.toString().trim() !== "" &&
                                              entry?.unit?.toString().trim() !== ""
                                          )
                                        ) ||
                                        (addProductFormData[index]?.usePhaseFuelEntries || []).length >= 2
                                      }
                                      sx={{ width: "fit-content" }}
                                    >
                                      <AddIcon sx={{ mr: 0.5 }} />
                                      Add new
                                    </Button>
                                  </Box>
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Electricity Used for Maintenance
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder="Enter electricity used"
                                      value={
                                        addProductFormData[index]
                                          ?.electricityUsedForMaintenance || ""
                                      }
                                      onChange={(e) =>
                                        handleAddProductFieldChange(
                                          index,
                                          "electricityUsedForMaintenance",
                                          e.target.value
                                        )
                                      }
                                      inputProps={{ inputMode: "decimal" }}
                                      error={Boolean(addProductFormErrors[index]?.electricityUsedForMaintenance)}
                                      helperText={addProductFormErrors[index]?.electricityUsedForMaintenance || ""}
                                    />
                                    <TextField size="small" value="Mwh" disabled sx={{ width: "7rem" }} />
                                  </Box>
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Fuel Used for Maintenance (You can select upto two fuel type)
                                  </Typography>
                                  <Box display="flex" gap={1}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: "bold", flex: 1.3, minWidth: 180, mb: 0.1 }}
                                    >
                                      Fuel Name
                                    </Typography>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: "bold", flex: 1, minWidth: 150, mb: 0.1 }}
                                    >
                                      Quantity
                                    </Typography>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: "bold", flex: 0.8, minWidth: 130, mb: 0.1 }}
                                    >
                                      Unit
                                    </Typography>
                                  </Box>
                                  {(addProductFormData[index]?.maintenanceFuelEntries || [
                                    { fuelName: "", quantity: "", unit: "" },
                                  ]).map((fuel, fuelIndex) => (
                                    <Box
                                      display="flex"
                                      gap={1}
                                      key={`bulk-maintenance-fuel-${fuelIndex}`}
                                      sx={{ mb: 0.8 }}
                                    >
                                      <TextField
                                        select
                                        size="small"
                                        placeholder="Fuel Name"
                                        value={fuel?.fuelName || ""}
                                        onChange={(e) =>
                                          handleMaintenanceFuelEntryChange(
                                            index,
                                            fuelIndex,
                                            "fuelName",
                                            e.target.value
                                          )
                                        }
                                        sx={{ flex: 1.3, minWidth: 180 }}
                                        error={Boolean(
                                          addProductFormErrors[index]?.[
                                          `maintenanceFuelEntries.fuelName.${fuelIndex}`
                                          ]
                                        )}
                                        helperText={
                                          addProductFormErrors[index]?.[
                                          `maintenanceFuelEntries.fuelName.${fuelIndex}`
                                          ] || ""
                                        }
                                        SelectProps={{
                                          displayEmpty: true,
                                          renderValue: (selected) =>
                                            selected && selected !== "" ? selected : "Tap to select",
                                        }}
                                      >
                                        <MenuItem value="">Select fuel type</MenuItem>
                                        {FUEL_TYPE_OPTIONS.map((fuelType) => (
                                          <MenuItem
                                            key={fuelType}
                                            value={fuelType}
                                            disabled={getSelectedFuelNames(
                                              addProductFormData[index]?.maintenanceFuelEntries || [],
                                              fuelIndex
                                            ).has(fuelType)}
                                          >
                                            {fuelType}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                      <TextField
                                        size="small"
                                        placeholder="Quantity"
                                        value={fuel?.quantity || ""}
                                        onChange={(e) =>
                                          handleMaintenanceFuelEntryChange(
                                            index,
                                            fuelIndex,
                                            "quantity",
                                            e.target.value
                                          )
                                        }
                                        inputProps={{ inputMode: "decimal" }}
                                        sx={{ flex: 1, minWidth: 150 }}
                                        error={Boolean(
                                          addProductFormErrors[index]?.[
                                          `maintenanceFuelEntries.quantity.${fuelIndex}`
                                          ]
                                        )}
                                        helperText={
                                          addProductFormErrors[index]?.[
                                          `maintenanceFuelEntries.quantity.${fuelIndex}`
                                          ] || ""
                                        }
                                      />
                                      <TextField
                                        select
                                        size="small"
                                        placeholder="Unit"
                                        value={fuel?.unit || ""}
                                        onChange={(e) =>
                                          handleMaintenanceFuelEntryChange(
                                            index,
                                            fuelIndex,
                                            "unit",
                                            e.target.value
                                          )
                                        }
                                        sx={{ flex: 0.8, minWidth: 130 }}
                                        error={Boolean(
                                          addProductFormErrors[index]?.[
                                          `maintenanceFuelEntries.unit.${fuelIndex}`
                                          ]
                                        )}
                                        helperText={
                                          addProductFormErrors[index]?.[
                                          `maintenanceFuelEntries.unit.${fuelIndex}`
                                          ] || ""
                                        }
                                        SelectProps={{
                                          displayEmpty: true,
                                          renderValue: (selected) =>
                                            selected && selected !== "" ? selected : "Tap to select",
                                        }}
                                      >
                                        <MenuItem value="">Select unit</MenuItem>
                                        {(FUEL_UNIT_BY_TYPE[fuel?.fuelName] || []).map((u) => (
                                          <MenuItem key={u} value={u}>
                                            {u}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                      {(addProductFormData[index]?.maintenanceFuelEntries || []).length > 1 && (
                                        <IconButton
                                          color="error"
                                          onClick={() =>
                                            handleRemoveMaintenanceFuelEntry(index, fuelIndex)
                                          }
                                          sx={{ alignSelf: "center" }}
                                        >
                                          <Delete />
                                        </IconButton>
                                      )}
                                    </Box>
                                  ))}
                                  {addProductFormErrors[index]?.["maintenanceFuelEntries"] && (
                                    <Typography variant="caption" color="error">
                                      {addProductFormErrors[index]?.["maintenanceFuelEntries"]}
                                    </Typography>
                                  )}
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      variant="outlined"
                                      onClick={() => handleAddMaintenanceFuelEntry(index)}
                                      disabled={
                                        !(
                                          (addProductFormData[index]?.maintenanceFuelEntries || []).every(
                                            (entry) =>
                                              entry?.fuelName?.toString().trim() !== "" &&
                                              entry?.quantity?.toString().trim() !== "" &&
                                              entry?.unit?.toString().trim() !== ""
                                          )
                                        ) ||
                                        (addProductFormData[index]?.maintenanceFuelEntries || [])
                                          .length >= 2
                                      }
                                      sx={{ width: "fit-content" }}
                                    >
                                      <AddIcon sx={{ mr: 0.5 }} />
                                      Add new
                                    </Button>
                                  </Box>
                                </Box>                            <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Frequency of Maintenance per year
                                  </Typography>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Enter frequency"
                                    value={addProductFormData[index]?.frequency || ""}
                                    onChange={(e) =>
                                      handleAddProductFieldChange(index, "frequency", e.target.value)
                                    }
                                    inputProps={{ inputMode: "decimal" }}
                                    error={Boolean(addProductFormErrors[index]?.frequency)}
                                    helperText={addProductFormErrors[index]?.frequency || ""}
                                  />
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Expected Lifetime (years)
                                  </Typography>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Enter expected lifetime"
                                    value={addProductFormData[index]?.lifetime || ""}
                                    onChange={(e) =>
                                      handleAddProductFieldChange(index, "lifetime", e.target.value)
                                    }
                                    inputProps={{ inputMode: "decimal" }}
                                    error={Boolean(addProductFormErrors[index]?.lifetime)}
                                    helperText={addProductFormErrors[index]?.lifetime || ""}
                                  />
                                </Box>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Disposal Method
                                  </Typography>
                                  <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    SelectProps={{ displayEmpty: true }}
                                    value={addProductFormData[index]?.disposalMethod || ""}
                                    onChange={(e) =>
                                      handleAddProductFieldChange(
                                        index,
                                        "disposalMethod",
                                        e.target.value
                                      )
                                    }
                                    error={Boolean(addProductFormErrors[index]?.disposalMethod)}
                                    helperText={addProductFormErrors[index]?.disposalMethod || ""}
                                  >
                                    <MenuItem value="" disabled>
                                      Tap to Select
                                    </MenuItem>
                                    {(productCategoryOptions.find(
                                      (item) =>
                                        item.category === addProductFormData[index]?.category
                                    )?.methods || []
                                    ).map((method, idx) => (
                                      <MenuItem key={idx} value={method?.type}>
                                        {method?.type}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1", mt: 0.5 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 700,
                                      borderTop: "1px solid #e5e7eb",
                                      pt: 1.2,
                                    }}
                                  >
                                    Emission from Manufacturing Process
                                  </Typography>
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 , fontWeight: "bold"}}>
                                    Sample Used for Testing
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    gridColumn: "1 / -1",
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                    gap: 1.5,
                                  }}
                                >
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                                      Sample Basis
                                    </Typography>
                                    <TextField
                                      select
                                      size="small"
                                      fullWidth
                                      SelectProps={{ displayEmpty: true }}
                                      value={addProductFormData[index]?.sampleBasis || ""}
                                      onChange={(e) =>
                                        handleAddProductFieldChange(index, "sampleBasis", e.target.value)
                                      }
                                      error={Boolean(addProductFormErrors[index]?.sampleBasis)}
                                      helperText={addProductFormErrors[index]?.sampleBasis || ""}
                                    >
                                      <MenuItem value="" disabled>
                                        Tap to Select
                                      </MenuItem>
                                      {SAMPLE_BASIS_OPTIONS.map((basis) => (
                                        <MenuItem key={basis} value={basis}>
                                          {basis}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                                      Sample Quantity
                                    </Typography>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      placeholder="Enter sample quantity"
                                      value={addProductFormData[index]?.sampleQuantity || ""}
                                      onChange={(e) => {
                                        const { value } = e.target;
                                        if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
                                        handleAddProductFieldChange(
                                          index,
                                          "sampleQuantity",
                                          value
                                        );
                                      }}
                                      inputProps={{ inputMode: "decimal" }}
                                      error={Boolean(addProductFormErrors[index]?.sampleQuantity)}
                                      helperText={addProductFormErrors[index]?.sampleQuantity || ""}
                                    />
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                                      Unit
                                    </Typography>
                                    <TextField
                                      select
                                      size="small"
                                      fullWidth
                                      disabled={!addProductFormData[index]?.sampleBasis}
                                      SelectProps={{ displayEmpty: true }}
                                      value={
                                        (SAMPLE_UNITS_BY_BASIS[addProductFormData[index]?.sampleBasis] || [])
                                          .includes(addProductFormData[index]?.unit)
                                          ? addProductFormData[index]?.unit
                                          : ""
                                      }
                                      onChange={(e) =>
                                        handleAddProductFieldChange(index, "unit", e.target.value)
                                      }
                                      error={Boolean(addProductFormErrors[index]?.unit)}
                                      helperText={addProductFormErrors[index]?.unit || ""}
                                    >
                                      <MenuItem value="" disabled>
                                        Tap to Select
                                      </MenuItem>
                                      {(SAMPLE_UNITS_BY_BASIS[addProductFormData[index]?.sampleBasis] || []).map((unit) => (
                                        <MenuItem key={unit} value={unit}>
                                          {unit}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Box>
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1", mt: 1 }}>
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
                                      gap: 1,
                                      alignItems: "center",
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                      Process Name
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                      Emission to Air
                                    </Typography>
                                    <Box sx={{ width: 40, flexShrink: 0 }} aria-hidden />
                                  </Box>
                                  {(addProductFormData[index]?.processes || []).map(
                                    (process, processIndex) => (
                                      <Box
                                        key={`process-air-${processIndex}`}
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
                                          gap: 1,
                                          alignItems: "flex-start",
                                          mb: 1,
                                        }}
                                      >
                                        <TextField
                                          size="small"
                                          fullWidth
                                          placeholder="Process Name"
                                          value={process?.processName || ""}
                                          onChange={(e) =>
                                            handleProcessFieldChange(
                                              index,
                                              processIndex,
                                              "processName",
                                              e.target.value
                                            )
                                          }
                                          error={Boolean(
                                            addProductFormErrors[index]?.[
                                            `processes.processName.${processIndex}`
                                            ]
                                          )}
                                          helperText={
                                            addProductFormErrors[index]?.[
                                            `processes.processName.${processIndex}`
                                            ] || ""
                                          }
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                          <Autocomplete
                                            multiple
                                            disablePortal
                                            size="small"
                                            options={typeofemissions}
                                            disabled={!process?.processName}
                                            value={process?.airEmissions || []}
                                            onChange={(_, newValue) =>
                                              handleProcessFieldChange(
                                                index,
                                                processIndex,
                                                "airEmissions",
                                                newValue
                                              )
                                            }
                                            getOptionLabel={(option) => option}
                                            renderTags={() => null}
                                            renderInput={(params) => (
                                              <TextField
                                                {...params}
                                                variant="outlined"
                                                placeholder="Tap to Select"
                                                error={Boolean(
                                                  addProductFormErrors[index]?.[`airEmissions.${processIndex}`]
                                                )}
                                              />
                                            )}
                                            sx={{
                                              width: "100%",
                                              "& .MuiAutocomplete-inputRoot": {
                                                height: 40,
                                                alignItems: "center",
                                              },
                                              "& .MuiAutocomplete-tag": {
                                                display: "none",
                                              },
                                            }}
                                          />
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexWrap: "wrap",
                                              mt: 0.5,
                                            }}
                                          >
                                            {(process?.airEmissions || []).map((value) => (
                                              <Chip
                                                key={value}
                                                label={value}
                                                sx={{
                                                  bgcolor: "#1C486B",
                                                  color: "white",
                                                  height: "24px",
                                                  m: 0.25,
                                                  "& .MuiChip-deleteIcon": {
                                                    color: "white",
                                                    "&:hover": { color: "white" },
                                                  },
                                                }}
                                                onDelete={() =>
                                                  handleProcessFieldChange(
                                                    index,
                                                    processIndex,
                                                    "airEmissions",
                                                    (process?.airEmissions || []).filter(
                                                      (v) => v !== value
                                                    )
                                                  )
                                                }
                                              />
                                            ))}
                                          </Box>
                                        </Box>
                                        <IconButton
                                          onClick={() => handleRemoveProcess(index, processIndex)}
                                          disabled={
                                            (addProductFormData[index]?.processes || []).length <= 1
                                          }
                                          sx={{ alignSelf: "flex-start", mt: 0.5, color: "#9ca3af" }}
                                        >
                                          <Delete />
                                        </IconButton>
                                      </Box>
                                    )
                                  )}
                                  {(addProductFormData[index]?.processes || []).map(
                                    (_, pIdx) =>
                                      addProductFormErrors[index]?.[`airEmissions.${pIdx}`] ? (
                                        <Typography
                                          key={`air-err-${pIdx}`}
                                          variant="caption"
                                          color="error"
                                          sx={{ display: "block", mt: 0.5 }}
                                        >
                                          Row {pIdx + 1}: {addProductFormErrors[index][`airEmissions.${pIdx}`]}
                                        </Typography>
                                      ) : null
                                  )}
                                  <Box sx={{ mt: 1 }}>
                                    <Button
                                      variant="contained"
                                      onClick={() => handleAddProcess(index)}
                                      sx={{
                                        backgroundColor: "#1f4e79",
                                        textTransform: "uppercase",
                                        fontWeight: 600,
                                      }}
                                    >
                                      <AddIcon sx={{ mr: 0.5 }} />
                                      Add new process
                                    </Button>
                                  </Box>
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1", mt: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Emission Release in Processes
                                  </Typography>
                                  <AddTables
                                    title=""
                                    formData={{
                                      ...(addProductFormData[index] || {}),
                                      processes: getSafeProcessesForAddTable(index),
                                    }}
                                    table={
                                      Array.isArray(addProductTableData[index]) &&
                                        addProductTableData[index].length > 0
                                        ? addProductTableData[index]
                                        : buildProcessTable(getSafeProcessesForAddTable(index))
                                    }
                                    units={PROCESS_EMISSION_UNITS}
                                    inputData={PROCESS_TABLE_INPUT}
                                    disabled={false}
                                    defaultSegments={[]}
                                    errors={getProcessTableErrors(index)}
                                    setErrors={(updater) =>
                                      setAddProductFormErrors((prev) => {
                                        const currentRowErrors = prev[index] || {};
                                        const preservedNonTableErrors = Object.fromEntries(
                                          Object.entries(currentRowErrors).filter(
                                            ([key]) => !isProcessTableErrorKey(key)
                                          )
                                        );
                                        const nextTableErrors =
                                          typeof updater === "function"
                                            ? updater(getProcessTableErrors(index))
                                            : updater || {};
                                        return {
                                          ...prev,
                                          [index]: {
                                            ...preservedNonTableErrors,
                                            ...nextTableErrors,
                                          },
                                        };
                                      })
                                    }
                                    setFormData={(updater) =>
                                      setAddProductFormData((prev) => ({
                                        ...prev,
                                        [index]:
                                          typeof updater === "function"
                                            ? updater(prev[index] || {})
                                            : updater,
                                      }))
                                    }
                                    setTable={(updater) =>
                                      setAddProductTableData((prev) => ({
                                        ...prev,
                                        [index]:
                                          typeof updater === "function"
                                            ? updater(prev[index] || [])
                                            : updater,
                                      }))
                                    }
                                  />
                                </Box>
                              </Box>
                              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mb: 2 }}>
                                <Button
                                  variant="contained"
                                  onClick={() => handleAddProductItem(index)}
                                >
                                  Add Product
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

         <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Document Upload
            </Typography>
            <Box sx={{ width: "100%" }}>
              <FormControl
                component="fieldset"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                  m: 0,
                }}
              >
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, color: "text.primary", fontSize: "0.875rem" }}
                >
                  Do you want to upload document?
                </FormLabel>
                <RadioGroup
                  value={documentUploadConfirmed ? "yes" : "no"}
                  onChange={(e) =>
                    handleDocumentUploadConfirmation(e.target.value === "yes")
                  }
                  row
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Box>

            {documentUploadConfirmed && (
              <Box
                sx={{
                  width: "100%",
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <FormControl
                  component="fieldset"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    m: 0,
                  }}
                >
                  <FormLabel
                    component="legend"
                    sx={{ mb: 1, color: "text.primary", fontSize: "0.875rem" }}
                  >
                    Document Upload Type
                  </FormLabel>
                  <RadioGroup
                    value={documentUploadType}
                    onChange={handleDocumentUploadTypeChange}
                    row
                    sx={{ flexWrap: "wrap", gap: 0.5 }}
                  >
                    <FormControlLabel
                      value="same"
                      control={<Radio />}
                      label="Same File for All Records"
                    />
                    <FormControlLabel
                      value="individual"
                      control={<Radio />}
                      label="Individual File for Each Record"
                    />
                  </RadioGroup>
                </FormControl>

                {documentUploadType === "same" && (
                  <Box sx={{ mt: 2, width: "100%" }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Upload Common Document:
                    </Typography>
                    <BulkFileUpload
                      handleChange={handleCommonDocumentChange}
                      handleClear={handleCommonDocumentClear}
                      filesList={commonDocument}
                    />
                  </Box>
                )}

                {documentUploadType === "individual" && (
                  <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    Upload individual documents for each record. Documents will be
                    added as a new column in the table above.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#ffebee",
                borderRadius: 1,
                border: "1px solid #f44336",
              }}
            >
              <Typography
                variant="h6"
                color="error"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Error:
              </Typography>
              {uploadValidationErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  color="error"
                  sx={{ mb: 0.5 }}
                >
                  - {error}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="primary"
            disabled={disableConfirm || uploading}
          >
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showNoDocumentDialog}
        onClose={() => setShowNoDocumentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">No Documents Uploaded</Typography>
            <IconButton onClick={() => setShowNoDocumentDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You selected document upload but no files were attached.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to proceed without uploading documents?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={() => setShowNoDocumentDialog(false)}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
          >
            Upload Documents
          </Button>
          <Button
            onClick={() => {
              setShowNoDocumentDialog(false);
              performUpload();
            }}
            variant="contained"
            color="primary"
          >
            Proceed Without Documents
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadReview;
